/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, January 9th 2022, 7:40:43 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import InputBase from "@mui/material/InputBase";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useDebounceFn } from "lib/utils";

type SearchFunction = (search_text: string) => void;

export interface AdditionalSearchProps {
  handleSearch?: SearchFunction;
  debounceSearch?: boolean;
  useSearchSubmit?: boolean;
}

interface SearchInputProps {
  showSearch?: boolean;
  placeholder_text?: string;
  extraSearchProps?: AdditionalSearchProps;
}

const defaultExtraSearchProps: AdditionalSearchProps = {
  debounceSearch: true,
  useSearchSubmit: true,
};

export const SearchInputWidget = ({
  showSearch = true,
  placeholder_text,
  extraSearchProps = defaultExtraSearchProps,
}: SearchInputProps) => {
  const [searchText, setSearchText] = React.useState<string>(null);

  const doSearch = () => extraSearchProps?.handleSearch && extraSearchProps.handleSearch(searchText);

  const clearInputText = () => {
    setSearchText("");
  };

  useDebounceFn(doSearch, {
    timeout: 500,
    deps: [searchText],
    doDebounce: extraSearchProps.debounceSearch,
  });

  const showClearIcon = searchText;
  const dividerStyle: React.ComponentProps<typeof Divider>["sx"] = showClearIcon
    ? { height: 28, m: 0.5 }
    : { height: 28, m: 0.5, borderColor: "#fff" };

  return (
    <Paper
      component="form"
      sx={{ p: "2px 4px", display: "flex", alignItems: "center", width: 400 }}
      style={{ visibility: showSearch ? "visible" : "hidden" }}
      onSubmit={(event) => event.preventDefault()}
    >
      <InputBase
        sx={{ ml: 1, flex: 1 }}
        placeholder={placeholder_text ? placeholder_text : "Search Here..."}
        inputProps={{ "aria-label": "search" }}
        value={searchText}
        onChange={(event) => {
          setSearchText(event.target.value);
        }}
      />

      {extraSearchProps.useSearchSubmit ? (
        <IconButton type="submit" sx={{ p: "10px" }} aria-label="search">
          <SearchIcon />
        </IconButton>
      ) : null}

      <Divider sx={dividerStyle} orientation="vertical" />
      {showClearIcon ? (
        <Tooltip title="Clear search" placement="right-end">
          <IconButton color="primary" sx={{ p: "10px" }} onClick={clearInputText}>
            <ClearIcon />
          </IconButton>
        </Tooltip>
      ) : null}
    </Paper>
  );
};
